import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Hand, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function renderBody(body) {
  const preserveLeading = (s) =>
    s.replace(/(^|<br\/>)( +)/g, (_, prefix, spaces) =>
      prefix + "\u00A0".repeat(spaces.length)
    );
  return (body || "").split(/\n\n+/).map((para, i) => (
    <p
      key={i}
      dangerouslySetInnerHTML={{
        __html: preserveLeading(
          para
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/\*(.+?)\*/g, "<em>$1</em>")
            .replace(/\n/g, "<br/>")
        ),
      }}
    />
  ));
}

export default function Article() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);
  const [claps, setClaps] = useState(0);
  const [clapBurst, setClapBurst] = useState(false);
  const [form, setForm] = useState({ name: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/posts/${slug}`);
        setPost(data);
        setClaps(data.claps || 0);
        const { data: c } = await api.get(`/posts/${slug}/comments`);
        setComments(c);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const onClap = async () => {
    try {
      const { data } = await api.post(`/posts/${slug}/clap`);
      setClaps(data.claps);
      setClapBurst(true);
      setTimeout(() => setClapBurst(false), 500);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSubmitComment = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${slug}/comments`, form);
      setComments([data, ...comments]);
      setForm({ name: "", body: "" });
      toast.success("Thank you for the note.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDeleteComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments(comments.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (notFound) {
    return (
      <div className="px-6 py-32 text-center">
        <h1 className="font-serif text-5xl font-light">Not found</h1>
        <p className="mt-4 text-black/55">
          This entry does not exist — perhaps it was never written.
        </p>
        <Link
          to="/"
          className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] link-underline"
        >
          Return home
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="py-32 text-center text-sm text-black/40 tracking-widest uppercase">
        Loading…
      </div>
    );
  }

  return (
    <article className="pt-12 md:pt-20" data-testid="article-page">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <Link
            to={`/category/${encodeURIComponent(post.category)}`}
            className="inline-block text-[11px] uppercase tracking-[0.28em] text-black/55 link-underline"
            data-testid="article-category-link"
          >
            {post.category}
          </Link>
          <h1
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.03] tracking-tighter"
            data-testid="article-title"
          >
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
            <span>{formatDate(post.created_at)}</span>
            <span className="w-1 h-1 bg-black/30 rounded-full" />
            <span>{post.reading_minutes} min read</span>
          </div>
        </div>
      </div>

      {post.cover_image && (
        <div className="mt-12 mb-16 px-6 md:px-12 lg:px-24">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full max-h-[560px] object-cover grayscale"
          />
        </div>
      )}

      <div className="px-6 md:px-12 lg:px-24">
        <div className="article-prose max-w-2xl mx-auto" data-testid="article-body">
          {renderBody(post.body)}
        </div>
      </div>

      {/* Clap */}
      <div className="px-6 md:px-12 lg:px-24 mt-16">
        <div className="max-w-2xl mx-auto border-t border-b border-black/10 py-10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-black/50">
              Found something true here?
            </div>
            <div className="font-serif text-3xl mt-2 font-light">
              Leave an insight
            </div>
          </div>
          <button
            onClick={onClap}
            className={`group flex items-center gap-4 border border-black px-6 py-4 hover:bg-black hover:text-white transition-colors ${
              clapBurst ? "scale-105" : ""
            }`}
            style={{ transition: "transform 0.2s, background 0.3s, color 0.3s" }}
            data-testid="clap-button"
          >
            <Hand strokeWidth={1.25} className="w-5 h-5" />
            <span className="font-serif text-2xl" data-testid="clap-count">
              {claps}
            </span>
          </button>
        </div>
      </div>

      {/* Comments */}
      <section className="px-6 md:px-12 lg:px-24 mt-20" data-testid="comments-section">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-baseline justify-between mb-8">
            <h3 className="font-serif text-3xl font-normal">Reflections</h3>
            <span className="text-[10px] uppercase tracking-[0.28em] text-black/45">
              {comments.length} {comments.length === 1 ? "note" : "notes"}
            </span>
          </div>

          <form
            onSubmit={onSubmitComment}
            className="mb-12 border-b border-black/10 pb-12"
            data-testid="comment-form"
          >
            <input
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-black/20 py-3 focus:outline-none focus:border-black placeholder:text-black/30"
              required
              maxLength={80}
              data-testid="comment-name-input"
            />
            <textarea
              placeholder="Share a reflection…"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={4}
              className="w-full bg-transparent border-b border-black/20 py-3 mt-6 resize-none focus:outline-none focus:border-black placeholder:text-black/30"
              required
              maxLength={2000}
              data-testid="comment-body-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-black/85 transition-colors disabled:opacity-50"
              data-testid="comment-submit-btn"
            >
              {submitting ? "Sending…" : "Send"}
            </button>
          </form>

          <div className="space-y-10">
            {comments.length === 0 ? (
              <p className="font-serif italic text-black/50">
                No reflections yet. Be the first to write.
              </p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="border-l border-black/15 pl-6" data-testid={`comment-${c.id}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="font-serif text-xl">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-black/40">
                      {formatDate(c.created_at)}
                    </div>
                  </div>
                  <p className="mt-2 text-base text-black/75 leading-relaxed whitespace-pre-wrap">
                    {c.body}
                  </p>
                  {user && user.role === "admin" && (
                    <button
                      onClick={() => onDeleteComment(c.id)}
                      className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-black/50 hover:text-black"
                      data-testid={`comment-delete-${c.id}`}
                    >
                      <Trash2 strokeWidth={1.25} className="w-3 h-3" />
                      Delete
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </article>
  );
}
